// Reusable FLEXX logo that strips the baked-in black background at the pixel
// level (canvas-based) so it renders cleanly on any surface — hero, footer,
// PWA install toast, auth screens. Falls back to the original /flexx-logo.png
// while the canvas pass is in flight or if it fails.

import React, { useEffect, useState } from "react";

const SRC = "/flexx-logo.png";

// Module-level cache so the canvas pass only runs once per page load.
let cachedDataUrl = null;
let cachedPromise = null;

function loadTransparentLogo() {
  if (cachedDataUrl) return Promise.resolve(cachedDataUrl);
  if (cachedPromise) return cachedPromise;
  cachedPromise = new Promise((resolve) => {
    const img = new Image();
    // NOTE: /flexx-logo.png is same-origin, so we skip crossOrigin="anonymous"
    // — a PWA service-worker cached response without CORS headers will fail
    // to load with that attribute set, which prevents the canvas alpha-strip
    // from running on repeat visits.
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || 400;
        canvas.height = img.naturalHeight || 400;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const pix = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = pix.data;
        for (let i = 0; i < d.length; i += 4) {
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          // Dark (<42) → transparent; mid (42–95) → fade out smoothly.
          if (lum < 42) {
            d[i + 3] = 0;
          } else if (lum < 95) {
            d[i + 3] = Math.floor(d[i + 3] * ((lum - 42) / 53));
          }
        }
        ctx.putImageData(pix, 0, 0);
        cachedDataUrl = canvas.toDataURL("image/png");
        resolve(cachedDataUrl);
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = SRC;
  });
  return cachedPromise;
}

export default function FlexxLogoImg({ style, alt = "FLEXX", ...rest }) {
  const [dataUrl, setDataUrl] = useState(cachedDataUrl);
  useEffect(() => {
    if (cachedDataUrl) return;
    let cancelled = false;
    loadTransparentLogo().then((url) => {
      if (!cancelled && url) setDataUrl(url);
    });
    return () => { cancelled = true; };
  }, []);
  return (
    <img
      src={dataUrl || SRC}
      alt={alt}
      style={style}
      onError={(e) => { e.target.style.display = "none"; }}
      {...rest}
    />
  );
}
